import React, { useMemo, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography, Tooltip, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import moralejaImg from '../../imagenes/Centros/moraleja.png';
import pozueloImg from '../../imagenes/Centros/pozuelo.png';
import plazacastillaImg from '../../imagenes/Centros/plazacastilla.png';
import otherImg from '../../imagenes/Centros/other.png';
import { colorForCentro } from '../../pages/admin/manager/disponibilidad/colorForCentro';
import { useTranslation } from 'react-i18next';

/** Tipo compatible con tus filas de disponibilidad */
export type Row = {
  id: number;
  diaSemana: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  horaInicio: string;
  horaFin: string;
  comentario?: string | null;
  idCentro?: number | null;
  formatosMask?: number;
  vigenciaDesde?: string | null;
  vigenciaHasta?: string | null;
  bgSnapshot?: string;
  _centrosArray?: number[]; // Array de centros para múltiples centros
  _centrosConsolidados?: number[]; // Centros consolidados de filas duplicadas
  _centrosTouched?: boolean; // Marca si el usuario editó explícitamente los centros
};

const centerIconMap: Record<number, string> = {
  1: moralejaImg,
  2: plazacastillaImg,
  3: pozueloImg,
  4: otherImg,
};

const fmt24 = (hhmm: string) => dayjs(`2000-01-01 ${hhmm.slice(0, 5)}`).format('HH:mm');

/* Utils tiempo */
const toMinutes = (hms: string) => {
  const [h, m] = hms.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
};
const mmToHHMM = (min: number) => {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));
const snap = (min: number, step: number) => Math.round(min / step) * step;
const snapDown = (min: number, step: number) => Math.floor(min / step) * step;
const snapUp = (min: number, step: number) => Math.ceil(min / step) * step;

/** Asigna carriles (lanes) por día para evitar solapes visuales */
function buildLanes(dayRows: Row[]) {
  const items = dayRows
    .map((r) => ({ ...r, start: toMinutes(r.horaInicio), end: toMinutes(r.horaFin) }))
    .sort((a, b) => a.start - b.start || a.end - b.end);

  type Lane = { end: number; rows: typeof items };
  const lanes: Lane[] = [];
  for (const it of items) {
    let placed = false;
    for (const lane of lanes) {
      if (lane.end <= it.start) {
        lane.rows.push(it);
        lane.end = it.end;
        placed = true;
        break;
      }
    }
    if (!placed) lanes.push({ end: it.end, rows: [it] });
  }
  return lanes.map((l) => l.rows);
}

// ---- TIME LAYOUT (no lineal) ----
type TimeSegment =
  | { fromMin: number; toMin: number; columns: number; label?: string } // bloque compacto
  | { fromMin: number; toMin: number; columnsPerHour: 1; hourLabel?: (h: number) => string }; // por horas

function totalColumns(layout: TimeSegment[]) {
  return layout.reduce((acc, seg) => {
    if ('columnsPerHour' in seg) return acc + ((seg.toMin - seg.fromMin) / 60) * seg.columnsPerHour;
    return acc + seg.columns;
  }, 0);
}
function buildHourMarkers(layout: TimeSegment[]) {
  const centers: number[] = [];
  const labels: string[] = [];
  let accCols = 0;
  for (const seg of layout) {
    if ('columnsPerHour' in seg) {
      const hours = (seg.toMin - seg.fromMin) / 60;
      for (let i = 0; i < hours; i++) {
        const hour = seg.fromMin / 60 + i;
        const center = accCols + i + 0.5;
        centers.push(center);
        labels.push(seg.hourLabel ? seg.hourLabel(hour) : `${hour}:00`);
      }
      accCols += hours * seg.columnsPerHour;
    } else {
      accCols += seg.columns;
    }
  }
  return { centers, labels };
}
/** minutos → columna flotante según layout */
function minutesToColumn(layout: TimeSegment[], minute: number) {
  let col = 0;
  for (const seg of layout) {
    const from = seg.fromMin;
    const to = seg.toMin;
    if (minute >= to) {
      col += 'columnsPerHour' in seg ? ((to - from) / 60) * seg.columnsPerHour : seg.columns;
      continue;
    }
    if (minute <= from) break;
    if ('columnsPerHour' in seg) {
      const minsInto = minute - from;
      col += (minsInto / 60) * seg.columnsPerHour;
    } else {
      const ratio = (minute - from) / (to - from);
      col += ratio * seg.columns;
    }
    break;
  }
  return col;
}
/** inversa aproximada: columna → minutos absolutos */
function columnToMinutes(layout: TimeSegment[], col: number) {
  let acc = 0;
  for (const seg of layout) {
    const segCols =
      'columnsPerHour' in seg ? ((seg.toMin - seg.fromMin) / 60) * seg.columnsPerHour : seg.columns;
    if (col >= acc + segCols) {
      acc += segCols;
      continue;
    }
    const inside = col - acc;
    if ('columnsPerHour' in seg) {
      const minsInto = inside * (60 / seg.columnsPerHour);
      return clamp(seg.fromMin + minsInto, seg.fromMin, seg.toMin);
    }
    const ratio = inside / seg.columns;
    return clamp(seg.fromMin + ratio * (seg.toMin - seg.fromMin), seg.fromMin, seg.toMin);
  }
  return layout[layout.length - 1].toMin;
}

/** texto de formatos: vacío si equivale al “todo” indicado por fullMaskForAll */
function formatText(mask: number | undefined, fullMaskForAll: number, t: (key: string) => string) {
  const ALL = fullMaskForAll || 7; // fallback
  const m = typeof mask === 'number' ? mask & ALL : ALL;
  if (m === ALL) return '';
  const list: string[] = [];
  if (m & 1) list.push(t('modalidad.presencial'));
  if (m & 2) list.push(t('modalidad.online'));
  if (m & 4) list.push(t('modalidad.a_domicilio'));
  return list.join(' + ');
}

/* ===== Colores auxiliares para el degradado ===== */
function lightenHex(hex: string, ratio = 0.18) {
  const val = hex.replace('#', '');
  const full =
    val.length === 3
      ? val
          .split('')
          .map((ch) => ch + ch)
          .join('')
      : val.padStart(6, '0');
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * ratio);
  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

function getBarBackground(r: Row, gradientColorsForAll: string[]) {
  // 1) Si hay snapshot (color o degradado) → SIEMPRE lo respetamos
  if (r.bgSnapshot) {
    const snap = String(r.bgSnapshot).trim();
    return snap.startsWith('linear-gradient') ? { background: snap } : { bgcolor: snap };
  }

  // 2) Si tiene _centrosArray (múltiples centros), crear degradado
  if (r._centrosArray && Array.isArray(r._centrosArray) && r._centrosArray.length > 0) {
    const hexes = r._centrosArray.map((id) => lightenHex(colorForCentro(id)));
    if (hexes.length >= 2) {
      return { background: buildLinearGradient(hexes) };
    } else if (hexes.length === 1) {
      return { bgcolor: hexes[0] };
    }
  }

  // 2b) Si tiene _centrosConsolidados (de filas duplicadas), crear degradado
  if (
    r._centrosConsolidados &&
    Array.isArray(r._centrosConsolidados) &&
    r._centrosConsolidados.length > 0
  ) {
    const hexes = r._centrosConsolidados.map((id) => lightenHex(colorForCentro(id)));
    if (hexes.length >= 2) {
      return { background: buildLinearGradient(hexes) };
    } else if (hexes.length === 1) {
      return { bgcolor: hexes[0] };
    }
  }

  // 3) Si no hay snapshot ni _centrosArray ni _centrosConsolidados: pintar según centro o "todos"
  if (r.idCentro != null) return { bgcolor: colorForCentro(r.idCentro) };

  if (gradientColorsForAll.length >= 2) {
    return { background: buildLinearGradient(gradientColorsForAll) };
  }
  if (gradientColorsForAll.length === 1) {
    return { bgcolor: gradientColorsForAll[0] };
  }
  return { bgcolor: colorForCentro(null) }; // fallback gris
}

function buildLinearGradient(colors: string[]) {
  if (!colors.length) return '';
  if (colors.length === 1) return colors[0];
  const step = 100 / (colors.length - 1);
  const stops = colors.map((c, i) => `${c} ${Math.round(i * step)}%`).join(', ');
  return `linear-gradient(90deg, ${stops})`;
}

type DOW = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type WeeklyBarsProps = {
  rows: Row[]; // para modo lectura
  centrosMap: Record<number, string>;
  titulo?: string;
  heightPerLane?: number;
  laneGap?: number;
  dayGap?: number;
  timeLayout?: TimeSegment[];
  mode?: 'time' | 'sequence';
  fullMaskForAll?: number;
  assignedCenterIds?: number[];

  /** ======= Edición ======= */
  editable?: boolean;
  value?: Row[];
  onChange?: (rows: Row[]) => void;
  stepMinutes?: number;
  newRowDefaults?: Partial<Row>;
  showDelete?: boolean;

  /** Doble click en fondo vacío de un día */
  onEmptyDoubleClick?: (day: Row['diaSemana'], approxMinute: number) => void;
  onEmptyClick?: (day: Row['diaSemana'], approxMinute: number) => void;
  /** Doble click en una barra existente */
  onRowDoubleClick?: (row: Row) => void;
  gradientCenterIdsForAll?: number[];
};

let __tempId = -1;

export default function WeeklyBars({
  rows,
  centrosMap,
  titulo,
  heightPerLane = 36,
  laneGap = 8,
  dayGap = 14,
  timeLayout,
  mode = 'time',
  fullMaskForAll = 7,
  assignedCenterIds,
  gradientCenterIdsForAll,

  // edición
  editable = false,
  value,
  onChange,
  stepMinutes = 15,
  newRowDefaults,
  showDelete,
  onEmptyDoubleClick,
  onEmptyClick,
  onRowDoubleClick,
}: WeeklyBarsProps) {
  const { t } = useTranslation<'translation'>();
  const translatedTitulo =
    titulo || t('disponibilidad.titulo', { defaultValue: 'Disponibilidad semanal' });
  const DAY_NAMES = [
    t('dias.lunes'),
    t('dias.martes'),
    t('dias.miercoles'),
    t('dias.jueves'),
    t('dias.viernes'),
    t('dias.sabado'),
    t('dias.domingo'),
  ];

  //  Respeta el timeLayout recibido; si no, 24h lineal
  const layout: TimeSegment[] = useMemo(
    () =>
      timeLayout && timeLayout.length
        ? timeLayout
        : [
            {
              fromMin: 0,
              toMin: 1440,
              columnsPerHour: 1 as const,
              hourLabel: (h: number) => dayjs().hour(h).minute(0).format('HH:mm'),
            },
          ],
    [timeLayout],
  );

  const columnsCount = useMemo(() => totalColumns(layout), [layout]);
  const pxPerCol = 56; // ancho por “columna” (1 columna = 1 hora)
  const contentPxWidth = Math.max(1200, Math.round(columnsCount * pxPerCol));

  const { centers: hourCenters, labels: hourLabels } = useMemo(
    () => buildHourMarkers(layout),
    [layout],
  );
  const hourBoundaries = useMemo(() => buildHourBoundaries(layout), [layout]);

  const currentRows = editable ? (value ?? rows) : rows;

  const byDay = useMemo<Record<DOW, Row[]>>(() => {
    const m: Record<DOW, Row[]> = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
    for (const r of currentRows ?? []) {
      m[r.diaSemana as DOW].push(r);
    }
    return m;
  }, [currentRows]);

  const dayRenderData = useMemo(() => {
    const arr: { dow: DOW; dayRows: Row[]; lanes: Row[][]; trackHeight: number }[] = [];
    ([0, 1, 2, 3, 4, 5, 6] as DOW[]).forEach((dow) => {
      const dayRows = (byDay[dow] ?? [])
        .slice()
        .sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
      const lanes = buildLanes(dayRows);
      const trackHeight = lanes.length
        ? lanes.length * heightPerLane + (lanes.length - 1) * laneGap
        : heightPerLane;
      arr.push({ dow, dayRows, lanes, trackHeight });
    });
    return arr;
  }, [byDay, heightPerLane, laneGap]);

  // Leyenda
  const centrosLegend = useMemo(() => {
    const ids =
      assignedCenterIds && assignedCenterIds.length
        ? assignedCenterIds
        : Array.from(
            new Set(
              (rows ?? []).filter((r) => r.idCentro != null).map((r) => r.idCentro as number),
            ),
          );
    const items: Array<{ id: number | null; name: string; isAll?: boolean }> = [
      { id: null, name: t('disponibilidad.todos_los_centros'), isAll: true },
      ...ids.map((id) => ({ id, name: centrosMap[id] ?? `#${id}` })),
    ];
    return items;
  }, [assignedCenterIds, rows, centrosMap, t]);

  // Colores para la leyenda "Todos"
  const legendColorsForAll = useMemo(() => {
    const ids =
      assignedCenterIds && assignedCenterIds.length
        ? assignedCenterIds
        : Array.from(
            new Set(
              (rows ?? []).filter((r) => r.idCentro != null).map((r) => r.idCentro as number),
            ),
          );
    return ids.map((id) => lightenHex(colorForCentro(id))).filter(Boolean);
  }, [assignedCenterIds, rows]);

  // 🎨 Colores que usarán las barras sin centro (degradado)
  const gradientColorsForAll = useMemo(() => {
    let sourceIds: number[] = [];

    if (editable) {
      // En modo edición el degradado viene de lo que la UI indique explícitamente
      sourceIds = (gradientCenterIdsForAll ?? []) as number[];
    } else {
      // En modo lectura: fallback habitual
      if (gradientCenterIdsForAll && gradientCenterIdsForAll.length) {
        sourceIds = gradientCenterIdsForAll as number[];
      } else if (assignedCenterIds && assignedCenterIds.length) {
        sourceIds = assignedCenterIds;
      } else {
        sourceIds = Array.from(
          new Set(rows.filter((r) => r.idCentro != null).map((r) => r.idCentro as number)),
        );
      }
    }

    const hexes = sourceIds.map((id) => lightenHex(colorForCentro(id)));
    return hexes.filter(Boolean);
  }, [editable, gradientCenterIdsForAll, assignedCenterIds, rows]);

  // ==== Edición: refs y estado de drag ====
  const dayRefs = useRef<(HTMLDivElement | null)[]>([]);
  const suppressCreateRef = useRef<number>(0);

  type Drag =
    | { kind: 'create'; day: DOW; startMin: number; currentMin: number }
    | { kind: 'resize-start'; day: DOW; id: number; start0: number; end0: number }
    | { kind: 'resize-end'; day: DOW; id: number; start0: number; end0: number }
    | {
        kind: 'move';
        day: DOW;
        id: number;
        start0: number;
        end0: number;
        pointerStartMin: number;
        anchorOffset: number;
      };
  const [drag, setDrag] = useState<Drag | null>(null);

  // util para emitir cambios
  const emit = (next: Row[]) => onChange && onChange(next);

  // helpers para posiciones
  const startEndOf = (r: Row) => [toMinutes(r.horaInicio), toMinutes(r.horaFin)] as const;

  // Generador de límites (líneas de hora)
  function buildHourBoundaries(layout: TimeSegment[]) {
    const bounds: number[] = [];
    let accCols = 0;
    for (const seg of layout) {
      if ('columnsPerHour' in seg) {
        const hours = (seg.toMin - seg.fromMin) / 60; // p.ej. 24 o 16 en tramo central
        for (let i = 0; i <= hours; i++) {
          bounds.push(accCols + i); // 0,1,2,...,hours
        }
        accCols += hours * seg.columnsPerHour;
      } else {
        accCols += seg.columns;
      }
    }
    return bounds;
  }

  // ----- utilidades de puntero -----
  function minuteFromXYInDay(clientX: number, day: DOW) {
    const el = dayRefs.current[day] as HTMLDivElement | null;
    const rect = el?.getBoundingClientRect();
    const width = rect?.width ?? 1;
    const left = rect?.left ?? 0;
    const x = clamp(clientX - left, 0, width);
    const col = (x / width) * columnsCount;
    const minute = columnToMinutes(layout, col);
    return minute;
  }
  function findDayAt(clientY: number): DOW | null {
    for (let d = 0 as DOW; d <= 6; d = (d + 1) as DOW) {
      const el = dayRefs.current[d];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientY >= r.top && clientY <= r.bottom) return d;
    }
    return null;
  }

  // ─── Handlers de edición ────────────────────────────────────────────────────
  const handleTrackMouseDown = (dow: DOW) => (ev: React.MouseEvent<HTMLDivElement>) => {
    if (!editable || !onChange) return;
    if (ev.target !== ev.currentTarget) return;
    if (suppressCreateRef.current > Date.now()) return;

    const rect = (ev.currentTarget as HTMLDivElement).getBoundingClientRect();
    const x = clamp(ev.clientX - rect.left, 0, rect.width);
    const col = (x / rect.width) * columnsCount;

    // minuto inicial alineado a rejilla
    const startMin = snap(columnToMinutes(layout, col), stepMinutes);

    // Umbral para diferenciar clic vs arrastre
    let moved = false;
    const DRAG_THRESHOLD = 4; // px

    setDrag({ kind: 'create', day: dow, startMin, currentMin: startMin + stepMinutes });

    const onMove = (e: MouseEvent) => {
      const mm = minuteFromXYInDay(e.clientX, dow); // sin snap aquí
      if (Math.abs(e.movementX) > DRAG_THRESHOLD) moved = true;
      setDrag((d) => (d && d.kind === 'create' ? { ...d, currentMin: mm } : d));
    };

    const onUp = (e: MouseEvent) => {
      const m2 = minuteFromXYInDay(e.clientX, dow); // sin snap
      setDrag(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);

      // Clic (sin arrastre): abre ventana si te pasan la prop
      if (!moved) {
        if (onEmptyClick) onEmptyClick(dow, startMin);
        return;
      }

      // Arrastre: crea franja alineando INICIO hacia abajo y FIN hacia arriba
      const aRaw = Math.min(startMin, m2);
      const bRaw = Math.max(startMin, m2);
      const start = snapDown(aRaw, stepMinutes);
      const end = snapUp(bRaw, stepMinutes);
      if (end - start < stepMinutes) return;

      // === IMPORTANTE ===
      // Decidimos el color/degradado que debe tener ESTA barra nueva
      // - Si el usuario ha elegido SOLO 1 centro (ej. Chamartín), pintamos ese color.
      // - Si ha elegido VARIOS centros, creamos un degradado con esos centros.
      // - Si ha elegido "todos", usamos el degradado de TODOS.
      // OJO: gradientColorsForAll ya contiene los colores (uno por centro activo en la UI)
      const guessBgSnapshot = (() => {
        if (newRowDefaults?.idCentro != null) {
          return colorForCentro(newRowDefaults.idCentro);
        }
        if (gradientColorsForAll && gradientColorsForAll.length > 1) {
          return buildLinearGradient(gradientColorsForAll);
        }
        if (gradientColorsForAll && gradientColorsForAll.length === 1) {
          return gradientColorsForAll[0];
        }
        return colorForCentro(null);
      })();

      // Creamos la nueva franja incluyendo su color/degradado congelado
      const newRow: Row = {
        id: __tempId--,
        diaSemana: dow,
        horaInicio: mmToHHMM(start),
        horaFin: mmToHHMM(end),
        comentario: (newRowDefaults?.comentario ?? null) as any,
        idCentro: (newRowDefaults?.idCentro ?? null) as any,
        formatosMask: newRowDefaults?.formatosMask,
        vigenciaDesde: newRowDefaults?.vigenciaDesde ?? null,
        vigenciaHasta: newRowDefaults?.vigenciaHasta ?? null,

        // 👇 CLAVE: guardamos el color/degradado de ESTA barra
        bgSnapshot: guessBgSnapshot,
      };

      emit([...(value ?? []), newRow]);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleDelete = (id: number) => () => {
    if (!editable || !onChange) return;
    emit((value ?? []).filter((r) => r.id !== id));
  };

  // Permite mover una barra existente
  const handleMove = (dow: DOW, row: Row) => (ev: React.MouseEvent<HTMLDivElement>) => {
    if (!editable || !onChange) return;
    ev.stopPropagation();
    ev.preventDefault();

    const [start0, end0] = startEndOf(row);
    const pointerStartMin = minuteFromXYInDay(ev.clientX, dow);
    const anchorOffset = pointerStartMin - start0;

    setDrag({
      kind: 'move',
      day: dow,
      id: row.id,
      start0,
      end0,
      pointerStartMin,
      anchorOffset,
    });

    const onMove = (e: MouseEvent) => {
      const minute = minuteFromXYInDay(e.clientX, dow);
      const delta = minute - pointerStartMin;
      const newStart = snapDown(start0 + delta, stepMinutes);
      const newEnd = snapUp(end0 + delta, stepMinutes);

      emit(
        (value ?? []).map((r) =>
          r.id === row.id
            ? {
                ...r,
                horaInicio: mmToHHMM(newStart),
                horaFin: mmToHHMM(newEnd),
              }
            : r,
        ),
      );
    };

    const onUp = () => {
      setDrag(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Permite redimensionar una barra (inicio o fin)
  const handleResize =
    (kind: 'resize-start' | 'resize-end', dow: DOW, row: Row) =>
    (ev: React.MouseEvent<HTMLDivElement>) => {
      if (!editable || !onChange) return;
      ev.stopPropagation();
      ev.preventDefault();

      const [start0, end0] = startEndOf(row);

      setDrag({ kind, day: dow, id: row.id, start0, end0 });

      const onMove = (e: MouseEvent) => {
        const minute = minuteFromXYInDay(e.clientX, dow);
        emit(
          (value ?? []).map((r) => {
            if (r.id !== row.id) return r;

            let newStart = start0;
            let newEnd = end0;

            if (kind === 'resize-start') {
              newStart = clamp(snapDown(minute, stepMinutes), 0, newEnd - stepMinutes);
            } else {
              newEnd = clamp(snapUp(minute, stepMinutes), newStart + stepMinutes, 24 * 60);
            }

            return {
              ...r,
              horaInicio: mmToHHMM(newStart),
              horaFin: mmToHHMM(newEnd),
            };
          }),
        );
      };

      const onUp = () => {
        setDrag(null);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

  const showDeleteIcon = showDelete ?? editable;

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        width: '100%',
        border: 1,
        borderColor: '#21A598',
        borderRadius: 2,
        p: 2,
      }}
    >
      {/* Título + leyenda de centros */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {centrosLegend.map((c) => {
            const isAll = c.isAll;
            const gradientCss =
              isAll && legendColorsForAll.length >= 2
                ? buildLinearGradient(legendColorsForAll)
                : isAll && legendColorsForAll.length === 1
                  ? legendColorsForAll[0]
                  : '';

            return (
              <Chip
                key={String(c.id) + (c.isAll ? '-all' : '')}
                label={c.name}
                size="small"
                icon={
                  !c.isAll && c.id != null ? (
                    <Box
                      component="img"
                      src={centerIconMap[c.id]}
                      alt=""
                      sx={{ width: 18, height: 18, objectFit: 'contain' }}
                    />
                  ) : undefined
                }
                sx={{
                  ...(gradientCss
                    ? { background: gradientCss }
                    : { bgcolor: colorForCentro(c.id) }),
                  color: 'black',
                  pl: !c.isAll && c.id != null ? '6px' : '0px',
                  '& .MuiChip-icon': {
                    marginLeft: '2px',
                    marginRight: '2px',
                    width: 'auto',
                    height: '20px',
                  },
                }}
              />
            );
          })}
        </Stack>
      </Stack>

      {/* ====== Layout 2 columnas: Izquierda fija (días), Derecha con scroll único ====== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr' }}>
        {/* Columna izquierda fija */}
        <Box>
          {/* Espaciador de cabecera (misma altura que la cabecera de horas) */}
          <Box sx={{ height: 52 }} />
          {/* Lista de días con su altura exacta */}
          <Stack spacing={dayGap / 8}>
            {dayRenderData.map(({ dow, trackHeight }) => (
              <Box
                key={`label-${dow}`}
                sx={{ height: trackHeight, display: 'flex', alignItems: 'flex-start' }}
              >
                <Box
                  sx={{
                    height: `${heightPerLane}px`,
                    lineHeight: `${heightPerLane}px`,
                    px: 1.25,
                    fontWeight: 700,
                    fontSize: 13,
                    bgcolor: '#26a69a',
                    color: 'white',
                    borderRadius: 1,
                    width: '100px',
                    textAlign: 'center',
                  }}
                >
                  {DAY_NAMES[dow]}
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Columna derecha: UN SOLO contenedor con overflowX -> scroll abajo */}
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Box sx={{ width: `${contentPxWidth}px` }}>
            {/* ---- Cabecera de horas (dentro del mismo scroll) ---- */}
            <Box sx={{ position: 'relative', height: 52 }}>
              {hourBoundaries.map((b, i) => {
                const leftPct = (b / columnsCount) * 100;
                return (
                  <Box
                    key={`head-line-${i}`}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      left: `${leftPct}%`,
                      width: 0,
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                      transform: 'translateX(-0.5px)',
                      pointerEvents: 'none',
                    }}
                  />
                );
              })}
              {hourCenters.map((c, i) => {
                const leftPct = (c / columnsCount) * 100;
                return (
                  <Box
                    key={`head-lbl-${i}`}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      left: `${leftPct}%`,
                      transform: 'translateX(-50%)',
                      textAlign: 'center',
                      lineHeight: 1.1,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 700, m: 0, p: 0 }}>
                      {hourLabels[i]}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* ---- Barras (mismo scroll) ---- */}
            <Box sx={{ position: 'relative' }}>
              {/* Líneas verticales a toda altura de las barras (en los límites exactos de hora) */}
              {hourBoundaries.map((b, i) => {
                const leftPct = (b / columnsCount) * 100;
                return (
                  <Box
                    key={`global-line-${i}`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: `${leftPct}%`,
                      width: 0,
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                      transform: 'translateX(-0.5px)',
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  />
                );
              })}

              <Stack sx={{ mt: 0, position: 'relative', zIndex: 1 }} spacing={dayGap / 8}>
                {dayRenderData.map(({ dow, lanes }) => {
                  const trackHeight = lanes.length
                    ? lanes.length * heightPerLane + (lanes.length - 1) * laneGap
                    : heightPerLane;

                  return (
                    <Box
                      key={`row-${dow}`}
                      sx={{ display: 'grid', gridTemplateColumns: '1fr', alignItems: 'stretch' }}
                    >
                      {/* Pista con lanes */}
                      <Box
                        ref={(el) => {
                          dayRefs.current[dow] = el as HTMLDivElement;
                        }}
                        sx={{
                          position: 'relative',
                          height: trackHeight,
                          cursor: editable ? 'crosshair' : 'default',
                        }}
                        onMouseDown={editable ? handleTrackMouseDown(dow) : undefined}
                        onDoubleClick={
                          editable && onEmptyDoubleClick
                            ? (e) => {
                                if (e.target !== e.currentTarget) return; // sólo fondo
                                suppressCreateRef.current = Date.now() + 300; // evita “crear” por el doble-clic
                                const rect = (
                                  e.currentTarget as HTMLDivElement
                                ).getBoundingClientRect();
                                const x = clamp(e.clientX - rect.left, 0, rect.width);
                                const col = (x / rect.width) * columnsCount;
                                const m = snap(columnToMinutes(layout, col), stepMinutes);
                                onEmptyDoubleClick(dow, m);
                              }
                            : undefined
                        }
                      >
                        {lanes.map((lane, laneIndex) =>
                          lane.map((r, idx) => {
                            const startCol = minutesToColumn(layout, toMinutes(r.horaInicio));
                            const endCol = minutesToColumn(layout, toMinutes(r.horaFin));
                            const leftPct = (startCol / columnsCount) * 100;
                            const widthPct = ((endCol - startCol) / columnsCount) * 100;

                            const base = `${fmt24(r.horaInicio)} – ${fmt24(r.horaFin)}`;
                            const fmtTxt = formatText(r.formatosMask, fullMaskForAll, t);
                            const label = fmtTxt ? `${base} · ${fmtTxt}` : base;

                            // 👇 NUEVO: decide el background ya congelado si existe
                            const barBgStyles = getBarBackground(r, gradientColorsForAll);

                            return (
                              <Tooltip
                                key={String(r.id ?? `${laneIndex}-${idx}`)}
                                title={
                                  <Box sx={{ p: 0.5 }}>
                                    <b>{DAY_NAMES[dow]}</b>
                                    <div>{label}</div>
                                  </Box>
                                }
                                arrow
                              >
                                <Box
                                  onMouseDown={editable ? handleMove(dow, r) : undefined}
                                  onDoubleClick={
                                    editable && onRowDoubleClick
                                      ? (e) => {
                                          e.stopPropagation();
                                          onRowDoubleClick(r);
                                        }
                                      : undefined
                                  }
                                  sx={{
                                    position: 'absolute',
                                    top: laneIndex * (heightPerLane + laneGap),
                                    left: `${leftPct}%`,
                                    width: `${widthPct}%`,
                                    height: heightPerLane,
                                    borderRadius: '5px',

                                    // 👇 AQUI aplicamos el snapshot si lo hay
                                    ...barBgStyles,

                                    boxSizing: 'border-box',
                                    border: '1px solid rgba(119, 119, 119, 1)',
                                    boxShadow: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 1.5,
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    fontSize: 13,
                                    fontWeight: 700,

                                    positionAnchor: 'left',
                                    cursor: editable ? 'grab' : 'default',
                                  }}
                                >
                                  {/* handles para resize */}
                                  {editable && (
                                    <>
                                      <Box
                                        onMouseDown={handleResize('resize-start', dow, r)}
                                        sx={{
                                          position: 'absolute',
                                          left: -6,
                                          top: '15%',
                                          bottom: '15%',
                                          width: 10,
                                          borderRadius: '6px',
                                          border: '2px solid rgba(0,0,0,0.35)',
                                          bgcolor: 'rgba(255,255,255,0.7)',
                                          cursor: 'ew-resize',
                                        }}
                                      />
                                      <Box
                                        onMouseDown={handleResize('resize-end', dow, r)}
                                        sx={{
                                          position: 'absolute',
                                          right: -6,
                                          top: '15%',
                                          bottom: '15%',
                                          width: 10,
                                          borderRadius: '6px',
                                          border: '2px solid rgba(0,0,0,.35)',
                                          bgcolor: 'rgba(255,255,255,.7)',
                                          cursor: 'ew-resize',
                                        }}
                                      />
                                    </>
                                  )}
                                  {label}

                                  {editable && onRowDoubleClick && (
                                    <Tooltip title="Editar" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onRowDoubleClick(r);
                                        }}
                                        sx={{
                                          position: 'absolute',
                                          top: 5,
                                          right: showDeleteIcon ? 55 : 30,
                                          width: 20,
                                          height: 20,
                                          bgcolor: '#fff',
                                          borderRadius: '50%',
                                          border: '0.5px solid #e4e4e4ff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          padding: 0,
                                          zIndex: 10,
                                          boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                                          '& svg': { fontSize: 14, color: '#21A598' },
                                          '&:hover': {
                                            bgcolor: '#21A598',
                                            '& svg': { color: '#fff' },
                                          },
                                        }}
                                      >
                                        <EditIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  )}

                                  {editable && showDeleteIcon && (
                                    <Tooltip title="Eliminar" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={handleDelete(r.id)}
                                        sx={{
                                          position: 'absolute',
                                          top: 5,
                                          right: 30,
                                          width: 20,
                                          height: 20,
                                          bgcolor: '#fff',
                                          borderRadius: '50%',
                                          border: '0.5px solid #e4e4e4ff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          padding: 0,
                                          zIndex: 10,
                                          boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                                          '& svg': { fontSize: 14, color: '#5a5c5bff' },
                                          '&:hover': {
                                            bgcolor: '#a52121ff',
                                            '& svg': { color: '#fff' },
                                          },
                                        }}
                                      >
                                        <CloseIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Tooltip>
                            );
                          }),
                        )}

                        {/* barra en creación (drag) */}
                        {drag &&
                          drag.kind === 'create' &&
                          drag.day === dow &&
                          (() => {
                            const a = Math.min(drag.startMin, drag.currentMin);
                            const b = Math.max(drag.startMin, drag.currentMin);
                            const startCol = minutesToColumn(layout, a);
                            const endCol = minutesToColumn(layout, b);
                            const leftPct = (startCol / columnsCount) * 100;
                            const widthPct = ((endCol - startCol) / columnsCount) * 100;
                            return (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: `${leftPct}%`,
                                  width: `${widthPct}%`,
                                  height: heightPerLane,
                                  borderRadius: '18px',
                                  background:
                                    'repeating-linear-gradient(45deg, rgba(33,165,152,.25), rgba(33,165,152,.25) 8px, rgba(33,165,152,.12) 8px, rgba(33,165,152,.12) 16px)',
                                  border: '2px dashed rgba(33,165,152,.6)',
                                  boxSizing: 'border-box',
                                  pointerEvents: 'none',
                                }}
                              />
                            );
                          })()}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
